import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaClock,
  FaCheck,
  FaTimes,
  FaEye,
  FaComment,
  FaFlag,
  FaUsers,
  FaFileAlt,
  FaChartLine,
  FaShieldAlt,
  FaExclamationTriangle,
  FaSearch,
  FaFilter,
  FaSort,
  FaThumbsUp,
  FaThumbsDown,
  FaSpinner,
  FaCheckCircle,
  FaBan,
  FaTrash,
  FaEdit,
  FaReply,
  FaFilePdf,
  FaFileWord,
  FaFilePowerpoint,
  FaDownload,
  FaHeart,
  FaRocket,
  FaArrowLeft,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import { FiTrendingUp, FiAward, FiBarChart2 } from "react-icons/fi";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";

const ModeratorDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [pendingNotes, setPendingNotes] = useState([]);
  const [reportedComments, setReportedComments] = useState([]);
  const [stats, setStats] = useState({
    totalNotes: 0,
    totalUsers: 0,
    totalDownloads: 0,
    pendingCount: 0,
    reportedCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [moderatingComment, setModeratingComment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  useEffect(() => {
    if (
      !isAuthenticated ||
      (user?.role !== "moderator" && user?.role !== "admin")
    ) {
      navigate("/dashboard");
      return;
    }
    fetchModeratorData();
    fetchStats();
  }, [isAuthenticated, user, navigate]);

  const fetchModeratorData = async () => {
    try {
      const token = localStorage.getItem("token");
      const [notesRes, commentsRes] = await Promise.all([
        axios.get("/api/admin/notes?status=pending", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("/api/admin/comments/reported", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPendingNotes(notesRes.data.notes || []);
      setReportedComments(commentsRes.data.comments || []);
    } catch (error) {
      console.error("Fetch moderator data error:", error);
      toast.error("Failed to fetch data");
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/api/admin/moderator-stats",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setStats({
        totalNotes: response.data.stats.totalNotes || 0,
        totalUsers: response.data.stats.totalUsers || 0,
        totalDownloads: response.data.stats.totalDownloads || 0,
        pendingCount: response.data.stats.pendingCount || 0,
        reportedCount: reportedComments.length,
      });
    } catch (error) {
      console.error("Fetch stats error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveNote = async (noteId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `/api/admin/notes/${noteId}/approve`,
        { comments: `Approved by ${user?.role}` },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        toast.success("Note approved successfully!");
        fetchModeratorData();
        fetchStats();
      }
    } catch (error) {
      console.error("Approve error:", error);
      toast.error(error.response?.data?.message || "Failed to approve note");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectNote = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `/api/admin/notes/${selectedNote._id}/reject`,
        { reason: rejectionReason, comments: `Rejected by ${user?.role}` },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        toast.success("Note rejected successfully");
        setShowRejectModal(false);
        setRejectionReason("");
        setSelectedNote(null);
        fetchModeratorData();
        fetchStats();
      }
    } catch (error) {
      console.error("Reject error:", error);
      toast.error(error.response?.data?.message || "Failed to reject note");
    } finally {
      setActionLoading(false);
    }
  };

  const handleModerateComment = async (commentId, action) => {
    setModeratingComment(commentId);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/admin/comments/${commentId}/moderate`,
        { action, moderationNote: `Moderated by ${user?.role}` },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Comment ${action === "keep" ? "kept" : "removed"} successfully`,
      );
      fetchModeratorData();
      fetchStats();
    } catch (error) {
      console.error("Moderate error:", error);
      toast.error("Failed to moderate comment");
    } finally {
      setModeratingComment(null);
    }
  };

  const getFileIcon = (type) => {
    const fileType = type?.toLowerCase() || "";
    if (fileType.includes("pdf"))
      return <FaFilePdf color="#ef4444" size={24} />;
    if (fileType.includes("doc"))
      return <FaFileWord color="#3b82f6" size={24} />;
    if (fileType.includes("ppt"))
      return <FaFilePowerpoint color="#f59e0b" size={24} />;
    return <FaFileAlt color="#8b5cf6" size={24} />;
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  const filteredNotes = pendingNotes.filter(
    (note) =>
      note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.subjectCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.subject?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const StatCard = ({ icon, title, value, color, trend }) => (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -5, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      style={{
        background: getCardBackground(),
        backdropFilter: "blur(20px)",
        borderRadius: "24px",
        padding: "1.5rem",
        boxShadow: isDarkMode
          ? "0 10px 30px -8px rgba(0,0,0,0.3)"
          : "0 10px 30px -8px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
        border: `1px solid ${getBorderColor()}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >
        <div>
          <p
            style={{
              color: getTextSecondary(),
              fontSize: "0.85rem",
              marginBottom: "0.5rem",
            }}
          >
            {title}
          </p>
          <h3
            style={{
              fontSize: "2rem",
              fontWeight: "bold",
              color: getTextColor(),
            }}
          >
            {value}
          </h3>
          {trend && (
            <p
              style={{
                color: "#10b981",
                fontSize: "0.75rem",
                marginTop: "0.5rem",
              }}
            >
              <FiTrendingUp style={{ marginRight: "0.2rem" }} /> {trend}
            </p>
          )}
        </div>
        <div
          style={{
            width: "50px",
            height: "50px",
            background: `${color}15`,
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: color,
            fontSize: "1.5rem",
          }}
        >
          {icon}
        </div>
      </div>
    </motion.div>
  );

  if (loading) return <Loader />;

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            borderRadius: "28px",
            padding: "2rem",
            marginBottom: "2rem",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            style={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background:
                "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
              borderRadius: "50%",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "0.5rem",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    width: "50px",
                    height: "50px",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <HiOutlineSparkles size={24} color="white" />
                </div>
                <h1
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  Moderator <span style={{ color: "#8b5cf6" }}>Dashboard</span>
                </h1>
              </div>
              <p style={{ color: "rgba(255,255,255,0.9)", marginLeft: "3rem" }}>
                Welcome back, {user?.name}. Review, approve, and moderate
                platform content.
              </p>
            </div>
            <div
              style={{
                background: "rgba(139,92,246,0.2)",
                padding: "0.5rem 1rem",
                borderRadius: "30px",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                backdropFilter: "blur(10px)",
              }}
            >
              <FaShieldAlt color="white" size={14} />
              <span
                style={{
                  color: "white",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                }}
              >
                Moderator Access
              </span>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1.5rem",
            marginBottom: "2rem",
          }}
        >
          <StatCard
            icon={<FaFileAlt />}
            title="Total Notes"
            value={stats.totalNotes}
            color="#8b5cf6"
          />
          <StatCard
            icon={<FaUsers />}
            title="Total Users"
            value={stats.totalUsers}
            color="#06b6d4"
          />
          <StatCard
            icon={<FaChartLine />}
            title="Total Downloads"
            value={stats.totalDownloads.toLocaleString()}
            color="#10b981"
          />
          <StatCard
            icon={<FaClock />}
            title="Pending Reviews"
            value={stats.pendingCount}
            color="#f59e0b"
            trend="Awaiting action"
          />
        </motion.div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            borderBottom: `2px solid ${getBorderColor()}`,
            marginBottom: "2rem",
            flexWrap: "wrap",
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            padding: "0.5rem 1rem",
            borderRadius: "20px",
          }}
        >
          {[
            {
              id: "pending",
              label: "Pending Notes",
              icon: <FaClock />,
              count: stats.pendingCount,
            },
            {
              id: "reported",
              label: "Reported Comments",
              icon: <FaFlag />,
              count: stats.reportedCount,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "0.8rem 1.5rem",
                background: "none",
                border: "none",
                borderBottom:
                  activeTab === tab.id ? "3px solid #8b5cf6" : "none",
                color: activeTab === tab.id ? "#8b5cf6" : getTextSecondary(),
                fontWeight: activeTab === tab.id ? "600" : "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.3s",
              }}
            >
              {tab.icon}
              {tab.label}
              {tab.count > 0 && (
                <span
                  style={{
                    background:
                      activeTab === tab.id
                        ? "#8b5cf6"
                        : isDarkMode
                          ? "rgba(139,92,246,0.2)"
                          : "#e2e8f0",
                    color: activeTab === tab.id ? "white" : getTextSecondary(),
                    padding: "0.2rem 0.5rem",
                    borderRadius: "20px",
                    fontSize: "0.7rem",
                    marginLeft: "0.3rem",
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{ marginBottom: "1.5rem" }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              padding: "0.5rem 1rem",
              borderRadius: "16px",
              border: `1px solid ${getBorderColor()}`,
              maxWidth: "400px",
            }}
          >
            <FaSearch color={getTextSecondary()} />
            <input
              type="text"
              placeholder="Search by title, subject code, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                fontSize: "0.9rem",
                background: "transparent",
                color: getTextColor(),
              }}
            />
          </div>
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === "pending" && (
              <div>
                {filteredNotes.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    style={{
                      textAlign: "center",
                      padding: "4rem",
                      background: getCardBackground(),
                      backdropFilter: "blur(20px)",
                      borderRadius: "24px",
                      border: `1px solid ${getBorderColor()}`,
                    }}
                  >
                    <FaCheckCircle size={50} color="#10b981" />
                    <h3 style={{ marginTop: "1rem", color: getTextColor() }}>
                      All Caught Up!
                    </h3>
                    <p style={{ color: getTextSecondary() }}>
                      No pending notes to review
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    style={{ display: "grid", gap: "1rem" }}
                  >
                    {filteredNotes.map((note, index) => (
                      <motion.div
                        key={note._id}
                        variants={itemVariants}
                        whileHover={{ y: -2 }}
                        style={{
                          background: getCardBackground(),
                          backdropFilter: "blur(20px)",
                          borderRadius: "20px",
                          padding: "1.5rem",
                          border: `1px solid ${getBorderColor()}`,
                          borderLeft: `4px solid #f59e0b`,
                          transition: "all 0.3s",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "1rem",
                            flexWrap: "wrap",
                          }}
                        >
                          {/* File Icon */}
                          <div
                            style={{
                              width: "60px",
                              height: "60px",
                              background: isDarkMode
                                ? "rgba(139,92,246,0.1)"
                                : "#f8f9fa",
                              borderRadius: "14px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {getFileIcon(note.fileType)}
                          </div>

                          {/* Note Details */}
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                marginBottom: "0.5rem",
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  background: "#f59e0b",
                                  color: "#333",
                                  padding: "0.2rem 0.6rem",
                                  borderRadius: "20px",
                                  fontSize: "0.7rem",
                                  fontWeight: "500",
                                }}
                              >
                                <FaClock
                                  size={10}
                                  style={{ marginRight: "0.3rem" }}
                                />
                                Pending Review
                              </span>
                              <span
                                style={{
                                  background: isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "#f0f0f0",
                                  color: getTextSecondary(),
                                  padding: "0.2rem 0.6rem",
                                  borderRadius: "20px",
                                  fontSize: "0.7rem",
                                }}
                              >
                                {note.branch}
                              </span>
                              <span
                                style={{
                                  background: isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "#f0f0f0",
                                  color: getTextSecondary(),
                                  padding: "0.2rem 0.6rem",
                                  borderRadius: "20px",
                                  fontSize: "0.7rem",
                                }}
                              >
                                Sem {note.semester}
                              </span>
                              {note.module && (
                                <span
                                  style={{
                                    background: isDarkMode
                                      ? "rgba(139,92,246,0.1)"
                                      : "#f0f0f0",
                                    color: getTextSecondary(),
                                    padding: "0.2rem 0.6rem",
                                    borderRadius: "20px",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Module {note.module}
                                </span>
                              )}
                            </div>
                            <h3
                              style={{
                                fontSize: "1.2rem",
                                color: getTextColor(),
                                marginBottom: "0.3rem",
                                fontWeight: "600",
                              }}
                            >
                              {note.title}
                            </h3>
                            <p
                              style={{
                                color: getTextSecondary(),
                                fontSize: "0.85rem",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {note.subject} ({note.subjectCode})
                            </p>
                            <p
                              style={{
                                color: getTextSecondary(),
                                fontSize: "0.8rem",
                                marginBottom: "0.3rem",
                              }}
                            >
                              Uploaded by: {note.uploadedBy?.name} (
                              {note.uploadedBy?.email})
                            </p>
                            <p
                              style={{
                                color: getTextSecondary(),
                                fontSize: "0.75rem",
                              }}
                            >
                              {new Date(note.createdAt).toLocaleString()}
                            </p>
                            {note.description && (
                              <p
                                style={{
                                  color: getTextSecondary(),
                                  fontSize: "0.85rem",
                                  marginTop: "0.5rem",
                                  padding: "0.5rem",
                                  background: isDarkMode
                                    ? "rgba(139,92,246,0.05)"
                                    : "#f8f9fa",
                                  borderRadius: "10px",
                                  lineHeight: "1.4",
                                }}
                              >
                                {note.description.substring(0, 150)}
                                {note.description.length > 150 ? "..." : ""}
                              </p>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.5rem",
                              minWidth: "100px",
                            }}
                          >
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() =>
                                window.open(`/notes/${note._id}`, "_blank")
                              }
                              style={{
                                padding: "0.5rem 1rem",
                                background:
                                  "linear-gradient(135deg, #06b6d4, #0891b2)",
                                color: "white",
                                border: "none",
                                borderRadius: "10px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.5rem",
                                fontSize: "0.85rem",
                                transition: "all 0.2s",
                              }}
                            >
                              <FaEye /> Preview
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleApproveNote(note._id)}
                              disabled={actionLoading}
                              style={{
                                padding: "0.5rem 1rem",
                                background: actionLoading
                                  ? "#94a3b8"
                                  : "linear-gradient(135deg, #10b981, #059669)",
                                color: "white",
                                border: "none",
                                borderRadius: "10px",
                                cursor: actionLoading
                                  ? "not-allowed"
                                  : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.5rem",
                                fontSize: "0.85rem",
                                transition: "all 0.2s",
                                opacity: actionLoading ? 0.6 : 1,
                              }}
                            >
                              {actionLoading ? (
                                <FaSpinner
                                  style={{
                                    animation: "spin 1s linear infinite",
                                  }}
                                />
                              ) : (
                                <FaCheck />
                              )}
                              Approve
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => {
                                setSelectedNote(note);
                                setShowRejectModal(true);
                              }}
                              disabled={actionLoading}
                              style={{
                                padding: "0.5rem 1rem",
                                background: actionLoading
                                  ? "#94a3b8"
                                  : "linear-gradient(135deg, #ef4444, #dc2626)",
                                color: "white",
                                border: "none",
                                borderRadius: "10px",
                                cursor: actionLoading
                                  ? "not-allowed"
                                  : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.5rem",
                                fontSize: "0.85rem",
                                transition: "all 0.2s",
                                opacity: actionLoading ? 0.6 : 1,
                              }}
                            >
                              <FaTimes /> Reject
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </div>
            )}

            {activeTab === "reported" && (
              <div>
                {reportedComments.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    style={{
                      textAlign: "center",
                      padding: "4rem",
                      background: getCardBackground(),
                      backdropFilter: "blur(20px)",
                      borderRadius: "24px",
                      border: `1px solid ${getBorderColor()}`,
                    }}
                  >
                    <FaCheckCircle size={50} color="#10b981" />
                    <h3 style={{ marginTop: "1rem", color: getTextColor() }}>
                      Clean Community!
                    </h3>
                    <p style={{ color: getTextSecondary() }}>
                      No reported comments to review
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    style={{ display: "grid", gap: "1rem" }}
                  >
                    {reportedComments.map((comment, index) => (
                      <motion.div
                        key={comment._id}
                        variants={itemVariants}
                        whileHover={{ y: -2 }}
                        style={{
                          background: getCardBackground(),
                          backdropFilter: "blur(20px)",
                          borderRadius: "20px",
                          padding: "1.5rem",
                          border: `1px solid ${getBorderColor()}`,
                          borderLeft: `4px solid #ef4444`,
                          transition: "all 0.3s",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "start",
                            flexWrap: "wrap",
                            gap: "1rem",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                marginBottom: "0.8rem",
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  background: "#ef4444",
                                  color: "white",
                                  padding: "0.2rem 0.6rem",
                                  borderRadius: "20px",
                                  fontSize: "0.7rem",
                                  fontWeight: "500",
                                }}
                              >
                                <FaFlag
                                  size={10}
                                  style={{ marginRight: "0.3rem" }}
                                />
                                Reported
                              </span>
                              <span
                                style={{
                                  background: isDarkMode
                                    ? "rgba(139,92,246,0.1)"
                                    : "#f0f0f0",
                                  color: getTextSecondary(),
                                  padding: "0.2rem 0.6rem",
                                  borderRadius: "20px",
                                  fontSize: "0.7rem",
                                }}
                              >
                                {comment.reportCount || 1} reports
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.8rem",
                                marginBottom: "0.8rem",
                              }}
                            >
                              <img
                                src={
                                  comment.user?.profilePicture ||
                                  `https://ui-avatars.com/api/?name=${comment.user?.name}&background=8b5cf6&color=fff&size=35`
                                }
                                alt={comment.user?.name}
                                style={{
                                  width: "35px",
                                  height: "35px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                              <div>
                                <strong style={{ color: getTextColor() }}>
                                  {comment.user?.name}
                                </strong>
                                <span
                                  style={{
                                    color: getTextSecondary(),
                                    fontSize: "0.7rem",
                                    marginLeft: "0.5rem",
                                  }}
                                >
                                  {new Date(
                                    comment.createdAt,
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <p
                              style={{
                                color: getTextColor(),
                                lineHeight: "1.5",
                                marginBottom: "0.5rem",
                              }}
                            >
                              "{comment.content}"
                            </p>
                            <Link
                              to={`/notes/${comment.note?._id}`}
                              style={{
                                color: "#8b5cf6",
                                fontSize: "0.8rem",
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.3rem",
                              }}
                              target="_blank"
                            >
                              <FaEye size={12} /> View on Note
                            </Link>
                          </div>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() =>
                                handleModerateComment(comment._id, "keep")
                              }
                              disabled={moderatingComment === comment._id}
                              style={{
                                padding: "0.5rem 1rem",
                                background:
                                  moderatingComment === comment._id
                                    ? "#94a3b8"
                                    : "linear-gradient(135deg, #10b981, #059669)",
                                color: "white",
                                border: "none",
                                borderRadius: "10px",
                                cursor:
                                  moderatingComment === comment._id
                                    ? "not-allowed"
                                    : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                fontSize: "0.85rem",
                                transition: "all 0.2s",
                                opacity:
                                  moderatingComment === comment._id ? 0.6 : 1,
                              }}
                            >
                              {moderatingComment === comment._id ? (
                                <FaSpinner
                                  style={{
                                    animation: "spin 1s linear infinite",
                                  }}
                                />
                              ) : (
                                <FaThumbsUp />
                              )}
                              Keep
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() =>
                                handleModerateComment(comment._id, "remove")
                              }
                              disabled={moderatingComment === comment._id}
                              style={{
                                padding: "0.5rem 1rem",
                                background:
                                  moderatingComment === comment._id
                                    ? "#94a3b8"
                                    : "linear-gradient(135deg, #ef4444, #dc2626)",
                                color: "white",
                                border: "none",
                                borderRadius: "10px",
                                cursor:
                                  moderatingComment === comment._id
                                    ? "not-allowed"
                                    : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                fontSize: "0.85rem",
                                transition: "all 0.2s",
                                opacity:
                                  moderatingComment === comment._id ? 0.6 : 1,
                              }}
                            >
                              {moderatingComment === comment._id ? (
                                <FaSpinner
                                  style={{
                                    animation: "spin 1s linear infinite",
                                  }}
                                />
                              ) : (
                                <FaTrash />
                              )}
                              Remove
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Rejection Modal */}
        {showRejectModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              backdropFilter: "blur(4px)",
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                padding: "2rem",
                borderRadius: "24px",
                maxWidth: "500px",
                width: "90%",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <FaExclamationTriangle size={30} color="#f59e0b" />
                <h3 style={{ color: getTextColor() }}>Reject Note</h3>
              </div>

              <p style={{ color: getTextSecondary(), marginBottom: "1rem" }}>
                Rejecting: <strong>{selectedNote?.title}</strong>
              </p>

              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows="4"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "12px",
                  marginBottom: "1rem",
                  fontSize: "1rem",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                autoFocus
              />

              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "flex-end",
                }}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason("");
                    setSelectedNote(null);
                  }}
                  style={{
                    padding: "0.8rem 2rem",
                    background: isDarkMode
                      ? "rgba(100,116,139,0.3)"
                      : "#e2e8f0",
                    color: getTextColor(),
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "1rem",
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleRejectNote}
                  disabled={actionLoading}
                  style={{
                    padding: "0.8rem 2rem",
                    background: actionLoading
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #ef4444, #dc2626)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: actionLoading ? "not-allowed" : "pointer",
                    fontSize: "1rem",
                    opacity: actionLoading ? 0.6 : 1,
                  }}
                >
                  {actionLoading ? (
                    <FaSpinner
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                  ) : (
                    "Reject Note"
                  )}
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ModeratorDashboard;

